import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { RecordSelectorModal } from './RecordSelectorModal';

export function AddRecordModal({ 
    targetTable, columns, fks, isPolymorphicTable,
    editFormData, handleInputChange, handleSaveClick, onCancel,
    token, lockedFields = []
}) {
    const [showSelectorForCol, setShowSelectorForCol] = useState(null);

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 sm:p-8">
            <div className="bg-slate-900 border border-slate-700 shadow-2xl rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden relative">
                
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-800 bg-slate-800/50 flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                            <span className="material-symbols-outlined text-green-400">add_circle</span>
                            Add Record: <span className="text-green-400">{targetTable}</span>
                        </h2>
                    </div>
                    <button 
                        onClick={onCancel}
                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                        title="Cancel"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Body Form */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {columns.map(col => {
                        if (col.IsIdentity) return null; // Skip identity columns
                        
                        const colName = col.ColumnName;
                        const isFk = fks.some(fk => fk.ColumnName === colName);
                        const isLocked = lockedFields.includes(colName);
                        
                        // Polymorphic target table specific logic
                        if (isPolymorphicTable && colName === 'TargetTable') {
                            return (
                                <div key={colName} className="flex flex-col gap-1">
                                    <label className="text-sm font-semibold text-slate-300 flex items-center gap-1">
                                        {colName}
                                        {isLocked && <span className="material-symbols-outlined text-[14px] text-teal-500">lock</span>}
                                    </label>
                                    <input 
                                        type="text"
                                        value={editFormData[colName] || ''}
                                        onChange={(e) => handleInputChange(e, colName)}
                                        placeholder="Schema.Table"
                                        disabled={isLocked}
                                        className={`bg-slate-950 border text-slate-200 px-3 py-2 rounded focus:outline-none focus:border-teal-500 ${isLocked ? 'border-teal-800 bg-slate-900/50 opacity-80 cursor-not-allowed' : 'border-slate-700'}`}
                                    />
                                </div>
                            );
                        }

                        if (isFk) {
                            const fkDef = fks.find(f => f.ColumnName === colName);
                            return (
                                <div key={colName} className="flex flex-col gap-1">
                                    <label className="text-sm font-semibold text-slate-300 flex items-center gap-1">
                                        {colName} 
                                        {isLocked && <span className="material-symbols-outlined text-[14px] text-teal-500">lock</span>}
                                        <span className="text-teal-500 text-xs ml-1">(Link to {fkDef.RefTableName})</span>
                                    </label>
                                    <button 
                                        onClick={() => !isLocked && setShowSelectorForCol(colName)}
                                        disabled={isLocked}
                                        className={`w-full bg-slate-950 border text-slate-200 px-3 py-2 rounded transition-colors flex items-center justify-between ${isLocked ? 'border-teal-800 bg-slate-900/50 opacity-80 cursor-not-allowed' : 'border-slate-700 hover:bg-slate-800'}`}
                                    >
                                        <span className={editFormData[colName] ? 'text-slate-200' : 'text-slate-500 italic'}>
                                            {editFormData[colName] || 'Select Record...'}
                                        </span>
                                        <span className="material-symbols-outlined text-[18px]">
                                            {isLocked ? 'lock' : 'search'}
                                        </span>
                                    </button>

                                    {showSelectorForCol === colName && !isLocked && (
                                        <RecordSelectorModal 
                                            targetTable={fkDef.RefTableName}
                                            token={token}
                                            onCancel={() => setShowSelectorForCol(null)}
                                            onSelect={(selectedRecord) => {
                                                setShowSelectorForCol(null);
                                                handleInputChange({ target: { value: selectedRecord[fkDef.RefColumnName] } }, colName);
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        }

                        return (
                            <div key={colName} className="flex flex-col gap-1">
                                <label className="text-sm font-semibold text-slate-300 flex items-center gap-1">
                                    {colName}
                                    {isLocked && <span className="material-symbols-outlined text-[14px] text-teal-500">lock</span>}
                                </label>
                                <input 
                                    type="text"
                                    value={editFormData[colName] || ''}
                                    onChange={(e) => handleInputChange(e, colName)}
                                    disabled={isLocked}
                                    className={`bg-slate-950 border text-slate-200 px-3 py-2 rounded focus:outline-none focus:border-teal-500 ${isLocked ? 'border-teal-800 bg-slate-900/50 opacity-80 cursor-not-allowed' : 'border-slate-700'}`}
                                />
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-800 bg-slate-800/50 flex justify-end gap-3 shrink-0">
                    <button 
                        onClick={onCancel}
                        className="px-4 py-2 rounded text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={async () => {
                            await handleSaveClick();
                            onCancel();
                        }}
                        className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white font-bold transition-colors flex items-center gap-2"
                    >
                        <span className="material-symbols-outlined text-[18px]">save</span>
                        Save Record
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
